import React, { useEffect, useMemo, useState, useCallback } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { createService, updateService } from "../../../graphql/mutations";

import {
  Backdrop,
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Portal,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import {
  Button,
  Datagrid,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  TextField,
  TopToolbar,
  useList,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import {
  ImportIcon,
  CloseIcon,
  CancelIcon,
} from "../../../components/common/iconography";
import { SearchField } from "../../../components/common/SearchField";

import { logError, logHistory } from "../../../utils/appUtils";
import {
  UnitOptions,
  ResourceType,
  ServiceType,
} from "../../../utils/constants";

import { fetchActiveAccounts } from "../accounts/AccountUtils";
import AccordionDatagrid from "../../../components/common/AccordionDatagrid";
import {
  composeNewService,
  fetchServices,
  getDefaultTaxRate,
} from "./ServiceUtils";
import { ServiceUploadDialog } from "./ServiceUploadDialog";

const useStyles = makeStyles((theme) => ({
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: theme.palette.common.white,
  },
  dialogContent: {
    height: "90vh",
  },
  scrollableGrid: {
    overflowX: "auto",
    overflowY: "hidden",
    width: "100%",
    border: "1px solid #ccc",
    borderRadius: 4,
    paddingBottom: 8,
    "&::-webkit-scrollbar": {
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  },
  dialogTitle: {
    display: "flex",
    paddingTop: "5px",
    paddingBottom: "5px",
    justifyContent: "space-between",
  },
}));

const AccountSearchField = (props) => {
  const { allAccounts, setAccounts, label, alwaysOn } = props;

  const handleSearch = (value) => {
    const searchTerm = value?.toLowerCase() || "";
    const filtered = allAccounts.filter(
      (acc) =>
        acc.displayName?.toLowerCase().includes(searchTerm) ||
        acc.businessName?.toLowerCase().includes(searchTerm) ||
        acc.lotNumberPrefix?.toLowerCase().includes(searchTerm) ||
        acc.addressState?.toLowerCase().includes(searchTerm),
    );
    setAccounts(filtered);
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
      searchOnChange={true}
    />
  );
};

const getListFilters = (allAccounts, setAccounts) => [
  <AccountSearchField
    label="Account Search"
    allAccounts={allAccounts}
    setAccounts={setAccounts}
    alwaysOn
  />,
];

const normalizeName = (name) => name?.trim().toLowerCase() || "";

const AccountServicesExpand = ({
  accountId,
  onSelectedChange,
  existingServiceNames = [],
}) => {
  const notify = useNotify();
  const theme = useTheme();
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const overlapSet = useMemo(() => {
    return new Set(existingServiceNames.map(normalizeName));
  }, [existingServiceNames]);
  const hasOverlap = overlapSet.size > 0;

  useEffect(() => {
    const load = async () => {
      if (!accountId) return;
      try {
        setLoading(true);
        const data = await fetchServices(accountId, true);
        setAllServices(data);
      } catch (error) {
        logError(
          "services:ImportServicesMenuButton:AccountServicesExpand:fetch",
          error,
          notify,
        );
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const ids = useMemo(() => allServices.map((s) => s.id), [allServices]);
  const listContext = useList({
    data: allServices,
    ids,
    loaded: !loading,
    loading,
    basePath: "/services",
    resource: "services",
    perPage: Math.max(allServices.length, 1),
    sort: { field: "name", order: "ASC" },
  });

  useEffect(() => {
    const ids = Array.isArray(listContext.selectedIds)
      ? listContext.selectedIds
      : [];
    onSelectedChange && onSelectedChange(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext.selectedIds]);

  return (
    <ListContextProvider value={listContext}>
      <Datagrid
        rowClick="toggleSelection"
        hasBulkActions
        rowStyle={(row) =>
          overlapSet.has(normalizeName(row?.name))
            ? { backgroundColor: theme.palette.action.selected }
            : {}
        }
      >
        <TextField source="name" label="Service Name" />
        <TextField source="description" label="Description" />
        <TextField source="status" label="Status" />
        {hasOverlap && (
          <FunctionField
            label="Overlap"
            render={(row) =>
              overlapSet.has(normalizeName(row?.name)) ? "Replace" : ""
            }
          />
        )}
      </Datagrid>
    </ListContextProvider>
  );
};

const AccountsListDialog = ({
  targetAccount,
  open,
  onClose,
  onSubmit,
  existingServices = [],
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const existingServiceNames = useMemo(() => {
    return existingServices.map((service) => service?.name).filter(Boolean);
  }, [existingServices]);
  const [loading, setLoading] = useState(false);
  const [allAccounts, setAllAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        let data = await fetchActiveAccounts(notify);
        if (targetAccount?.id) {
          data = data.filter((a) => a.id !== targetAccount.id);
        }
        setAllAccounts(data);
        setAccounts(data);
      } finally {
        setLoading(false);
      }
    };
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetAccount?.id]);

  const ids = useMemo(() => accounts.map((a) => a.id), [accounts]);
  const listContext = useList({
    data: accounts,
    ids,
    loaded: !loading,
    loading,
    basePath: "/accounts",
    resource: "accounts",
    perPage: 10,
    sort: { field: "displayName", order: "ASC" },
  });

  const handleConfirm = async () => {
    if (!expandedAccountId || !selectedServiceIds.length) return;
    try {
      const services = await fetchServices(expandedAccountId, true);
      const selected = services.filter((s) =>
        selectedServiceIds.includes(s.id),
      );
      const account = accounts.find((a) => a.id === expandedAccountId);
      onSubmit && onSubmit({ account, services: selected });
    } catch (error) {
      logError(
        "services:ImportServicesMenuButton:AccountsListDialog:confirm",
        error,
        notify,
      );
    }
  };

  return (
    <Dialog maxWidth="xl" open={open} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <h2> Select Source Account </h2>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <ListContextProvider value={listContext}>
          <ListToolbar
            filters={getListFilters(allAccounts, setAccounts)}
            actions={<TopToolbar />}
          />
          <AccordionDatagrid
            expand={
              <AccountServicesExpand
                accountId={expandedAccountId}
                onSelectedChange={setSelectedServiceIds}
                existingServiceNames={existingServiceNames}
              />
            }
            onToggleExpand={(nextId) => {
              setExpandedAccountId(nextId);
              setSelectedServiceIds([]);
            }}
            cleanupSelectionResource="services"
          >
            <TextField source="displayName" label="Display Name" />
            <TextField source="businessName" label="Account" />
            <TextField source="lotNumberPrefix" label="Lot No. Prefix" />
            <TextField source="addressState" label="State" />
          </AccordionDatagrid>
        </ListContextProvider>
      </DialogContent>
      <DialogActions>
        <Button label="Cancel" color="default" onClick={onClose}>
          <CancelIcon />
        </Button>
        <Button
          label="Submit"
          color="primary"
          onClick={handleConfirm}
          disabled={!selectedServiceIds.length}
        >
          <ImportIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const YardServicesDialog = ({
  open,
  onClose,
  onSubmit,
  existingServices = [],
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [allServices, setAllServices] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const existingServiceNames = useMemo(() => {
    return existingServices.map((service) => service?.name).filter(Boolean);
  }, [existingServices]);
  const overlapSet = useMemo(() => {
    return new Set(existingServiceNames.map(normalizeName));
  }, [existingServiceNames]);
  const hasOverlap = overlapSet.size > 0;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const yardId =
          window.appUser?.reloadYard?.id || window.appUser?.reloadYardId;
        if (!yardId) return;
        const data = await fetchServices(yardId, true);
        setAllServices(data);
        setServices(data);
      } catch (error) {
        logError(
          "services:ImportServicesMenuButton:YardServicesDialog:fetch",
          error,
          notify,
        );
      } finally {
        setLoading(false);
      }
    };
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ids = useMemo(() => services.map((s) => s.id), [services]);
  const listContext = useList({
    data: services,
    ids,
    loaded: !loading,
    loading,
    basePath: "/services",
    resource: "services",
    perPage: Math.max(services.length, 1),
    sort: { field: "name", order: "ASC" },
  });

  // Force-select ACCESSORIAL_CHARGES if present and prevent unselect
  const accessorialId = useMemo(() => {
    const svc = services.find(
      (s) => s.name === ServiceType.ACCESSORIAL_CHARGES,
    );
    return svc?.id;
  }, [services]);

  useEffect(() => {
    if (!accessorialId) return;
    setSelectedIds((prev) =>
      prev.includes(accessorialId) ? prev : [...prev, accessorialId],
    );
  }, [accessorialId]);

  useEffect(() => {
    // handle select all / deselect all from listContext
    const incoming = Array.isArray(listContext.selectedIds)
      ? listContext.selectedIds
      : [];

    // Ensure ACCESSORIAL_CHARGES remains selected if present
    let next = incoming;
    if (accessorialId && !incoming.includes(accessorialId)) {
      next = [...incoming, accessorialId];
    }

    // If user unselected all via header, keep accessorial selected (if present)
    if (incoming.length === 0 && accessorialId) {
      setSelectedIds([accessorialId]);
      return;
    }

    // De-duplicate and set
    const deduped = Array.from(new Set(next));
    setSelectedIds(deduped);
  }, [listContext.selectedIds, accessorialId]);

  const handleToggle = useCallback(
    (id) => {
      setSelectedIds((prev) => {
        if (id === accessorialId) {
          // Do not allow unselect; ensure it's included
          return prev.includes(id) ? prev : [...prev, id];
        }
        return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      });
    },
    [accessorialId],
  );

  const contextWithSelection = {
    ...listContext,
    selectedIds,
    onToggleItem: handleToggle,
  };

  const handleConfirm = () => {
    if (!selectedIds.length) return;
    const selected = services.filter((s) => selectedIds.includes(s.id));
    onSubmit && onSubmit(selected);
  };

  const handleSearch = (value) => {
    const searchTerm = value?.toLowerCase() || "";
    const filtered = allServices.filter(
      (s) =>
        s.name?.toLowerCase().includes(searchTerm) ||
        s.description?.toLowerCase().includes(searchTerm),
    );
    setServices(filtered);
  };

  return (
    <Dialog maxWidth="xl" open={open} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <h2> Select Services from MyYard </h2>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <ListContextProvider value={contextWithSelection}>
          <ListToolbar
            filters={[
              <SearchField
                id="serviceSearchTerm"
                label={"Search Services"}
                margin="dense"
                variant="outlined"
                alwaysOn
                handleSearch={handleSearch}
                searchOnChange={true}
              />,
            ]}
            actions={<TopToolbar />}
          />
          <div className={classes.scrollableGrid}>
            <Datagrid
              rowClick="toggleSelection"
              hasBulkActions
              rowStyle={(row) =>
                overlapSet.has(normalizeName(row?.name))
                  ? { backgroundColor: theme.palette.action.selected }
                  : {}
              }
            >
              <TextField source="name" label="Service Name" />
              <TextField source="description" label="Description" />
              <TextField source="status" label="Status" />
              {hasOverlap && (
                <FunctionField
                  label="Overlap"
                  render={(row) =>
                    overlapSet.has(normalizeName(row?.name)) ? "Replace" : ""
                  }
                />
              )}
            </Datagrid>
          </div>
        </ListContextProvider>
      </DialogContent>
      <DialogActions>
        <Button label="Cancel" color="default" onClick={onClose}>
          <CancelIcon />
        </Button>
        <Button
          label="Import"
          color="primary"
          onClick={handleConfirm}
          disabled={!selectedIds.length}
        >
          <ImportIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ImportServicesMenuButton = ({
  account,
  existingServices = [],
  handleRefresh,
  disabled,
  enableCSV = true,
  onCreateMerge,
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const form = useForm();
  const defaultTaxRate = getDefaultTaxRate(window.appUser?.reloadYard);
  const [anchorEl, setAnchorEl] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [yardOpen, setYardOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (!enableCSV) {
      // single option: open account selector directly
      setSelectOpen(true);
      return;
    }
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleCSV = () => {
    if (!enableCSV) return;
    setUploadOpen(true);
    handleClose();
  };

  const handleFromAccount = () => {
    setSelectOpen(true);
    handleClose();
  };

  const handleFromYard = () => {
    setYardOpen(true);
    handleClose();
  };

  const importFromAccount = async (payload) => {
    try {
      setIsSaving(true);
      const sourceAccount = payload?.account || payload;
      let sourceServices = payload?.services;
      if (!sourceServices) {
        sourceServices = await fetchServices(sourceAccount.id, true);
      }
      if (!account) {
        // CREATE flow: mirror CSV create process by updating local form state only
        const normalize = (s) => {
          const obj = {
            name: s.name,
            description: s.description || "",
            status: s.status,
            taxRate: s.taxRate || defaultTaxRate,
          };
          UnitOptions.forEach((unit) => {
            obj[unit.column] = s[unit.column] || 0;
          });
          return obj;
        };

        const base = existingServices.map(normalize);
        const byName = new Map(base.map((s) => [s.name, s]));
        let merged = [...base];
        sourceServices.forEach((srv) => {
          const item = normalize(srv);
          if (!byName.has(item.name)) merged.push(item);
          else {
            const idx = merged.findIndex((m) => m.name === item.name);
            if (idx >= 0) merged[idx] = item;
          }
        });
        // Update AccountCreate state only; form fields not used for create API
        onCreateMerge && onCreateMerge(merged);
        notify("Services imported successfully", "success");
      } else {
        // edit flow: create/update via API and reflect in form
        const oldServicesMap = existingServices?.length
          ? existingServices.reduce((acc, s) => {
              acc[s.name] = s;
              return acc;
            }, {})
          : {};

        const results = await Promise.allSettled(
          sourceServices.map((item) => {
            const oldService = oldServicesMap[item.name];
            if (oldService) {
              const input = { ...oldService };
              input.description = item.description || "";
              input.status = item.status;
              input.taxRate = item.taxRate || defaultTaxRate;
              UnitOptions.forEach((unit) => {
                input[unit.column] = item[unit.column] || 0;
              });
              delete input.createdAt;
              delete input.history;
              delete input.updatedAt;
              return API.graphql(graphqlOperation(updateService, { input }));
            }
            const input = composeNewService(item, account);
            return API.graphql(graphqlOperation(createService, { input }));
          }),
        );

        const resError = results.find((itm) => itm.status === "rejected");
        if (resError) throw resError.reason;

        const newServices = [];
        const updatedServiceMap = {};
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            const payload = result.value?.data;
            if (payload?.createService) newServices.push(payload.createService);
            if (payload?.updateService)
              updatedServiceMap[payload.updateService.id] =
                payload.updateService;
          }
        });

        // update form state for immediate UI feedback
        let newServiceList = existingServices.map((itm) =>
          updatedServiceMap[itm.id] ? updatedServiceMap[itm.id] : itm,
        );
        newServiceList = [...newServiceList, ...newServices];
        form.change("services.items", newServiceList);

        // history logs
        for (const service of newServices) {
          const historyNote = {
            parentId: service.id,
            parentType: ResourceType.SERVICE,
            text: "Service Created.",
          };
          logHistory(historyNote);
        }
        for (const service of Object.values(updatedServiceMap)) {
          const historyNote = {
            parentId: service.id,
            parentType: ResourceType.SERVICE,
            text: "Services bulk update.",
          };
          logHistory(historyNote);
        }

        notify("Services imported successfully", "success");
        handleRefresh && handleRefresh();
      }
    } catch (error) {
      logError(
        "services:ImportServicesMenuButton:importFromAccount",
        error,
        notify,
      );
    } finally {
      setIsSaving(false);
      setSelectOpen(false);
    }
  };

  const importFromYard = async (selectedServices) => {
    try {
      setIsSaving(true);
      if (!account) {
        // CREATE flow: merge into local form state (by name)
        const normalize = (s) => {
          const obj = {
            name: s.name,
            description: s.description || "",
            status: s.status,
            taxRate: s.taxRate || defaultTaxRate,
          };
          UnitOptions.forEach((unit) => {
            obj[unit.column] = s[unit.column] || 0;
          });
          return obj;
        };

        const base = existingServices.map(normalize);
        const byName = new Map(base.map((s) => [s.name, s]));
        let merged = [...base];
        selectedServices.forEach((srv) => {
          const item = normalize(srv);
          if (!byName.has(item.name)) merged.push(item);
          else {
            const idx = merged.findIndex((m) => m.name === item.name);
            if (idx >= 0) merged[idx] = item;
          }
        });
        onCreateMerge && onCreateMerge(merged);
        notify("Services imported successfully", "success");
      } else {
        // EDIT flow: create/update via API
        const oldServicesMap = existingServices?.length
          ? existingServices.reduce((acc, s) => {
              acc[s.name] = s;
              return acc;
            }, {})
          : {};

        const results = await Promise.allSettled(
          selectedServices.map((item) => {
            const oldService = oldServicesMap[item.name];
            if (oldService) {
              const input = { ...oldService };
              input.description = item.description || "";
              input.status = item.status;
              input.taxRate = item.taxRate || defaultTaxRate;
              UnitOptions.forEach((unit) => {
                input[unit.column] = item[unit.column] || 0;
              });
              delete input.createdAt;
              delete input.history;
              delete input.updatedAt;
              return API.graphql(graphqlOperation(updateService, { input }));
            }
            const input = composeNewService(item, account);
            return API.graphql(graphqlOperation(createService, { input }));
          }),
        );

        const resError = results.find((itm) => itm.status === "rejected");
        if (resError) throw resError.reason;

        const newServices = [];
        const updatedServiceMap = {};
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            const payload = result.value?.data;
            if (payload?.createService) newServices.push(payload.createService);
            if (payload?.updateService)
              updatedServiceMap[payload.updateService.id] =
                payload.updateService;
          }
        });

        let newServiceList = existingServices.map((itm) =>
          updatedServiceMap[itm.id] ? updatedServiceMap[itm.id] : itm,
        );
        newServiceList = [...newServiceList, ...newServices];
        form.change("services.items", newServiceList);

        for (const service of newServices) {
          const historyNote = {
            parentId: service.id,
            parentType: ResourceType.SERVICE,
            text: "Service Created.",
          };
          logHistory(historyNote);
        }
        for (const service of Object.values(updatedServiceMap)) {
          const historyNote = {
            parentId: service.id,
            parentType: ResourceType.SERVICE,
            text: "Services bulk update.",
          };
          logHistory(historyNote);
        }

        notify("Services imported successfully", "success");
        handleRefresh && handleRefresh();
      }
    } catch (error) {
      logError(
        "services:ImportServicesMenuButton:importFromYard",
        error,
        notify,
      );
    } finally {
      setIsSaving(false);
      setYardOpen(false);
    }
  };

  return (
    <>
      <MuiButton
        id="import-services-button"
        aria-controls={enableCSV && open ? "import-services-menu" : undefined}
        aria-haspopup={enableCSV ? "true" : undefined}
        aria-expanded={enableCSV && open ? "true" : undefined}
        onClick={handleClick}
        color="primary"
        size="small"
        disabled={disabled}
        className={classes.actionButton}
        startIcon={<ImportIcon />}
      >
        Import
      </MuiButton>
      {enableCSV && (
        <Menu
          id="import-services-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          getContentAnchorEl={null}
        >
          <MenuItem onClick={handleFromYard}>From MyYard</MenuItem>
          <MenuItem onClick={handleFromAccount}>From Account</MenuItem>
          <MenuItem onClick={handleCSV}>CSV</MenuItem>
        </Menu>
      )}

      {selectOpen && (
        <AccountsListDialog
          targetAccount={account}
          open={selectOpen}
          onClose={() => setSelectOpen(false)}
          onSubmit={importFromAccount}
          existingServices={existingServices}
        />
      )}

      {yardOpen && (
        <YardServicesDialog
          open={yardOpen}
          onClose={() => setYardOpen(false)}
          onSubmit={importFromYard}
          existingServices={existingServices}
        />
      )}

      {uploadOpen && (
        <ServiceUploadDialog
          account={account}
          existingServices={existingServices}
          uploadOpen={uploadOpen}
          setUploadOpen={setUploadOpen}
          handleRefresh={handleRefresh}
        />
      )}

      <Portal>
        <Backdrop open={isSaving} className={classes.backdrop}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Portal>
    </>
  );
};
